/**
 * DrawerFormKit — the presentation pieces shared by the admin create/edit
 * drawers (SIP Trunks, Outbound Caller ID, Transfer Rules, IVR …).
 *
 * These own layout and nothing else: `SectionCard` and `TabStrip` are pure
 * presentation, while `SwitchRow` / `CompactSwitch` bind a single boolean to
 * the surrounding `ZodForm` via `useFormContext`. Drop any control inside.
 *
 * Keeping them here rather than inline per drawer is what makes the drawers
 * read as one system — same card chrome, same tab strip, same toggle rows,
 * same accent.
 */
import { useId, useState, type JSX, type ReactNode } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputSwitch } from 'primereact/inputswitch'

/** House accent — matches the drawer header chip and Save button. */
export const ACCENT = '#3454cf'

// ---------------------------------------------------------------------------
// Section card
// ---------------------------------------------------------------------------

export interface SectionCardProps {
  title: string
  description: string
  /** PrimeIcons class for the header chip, e.g. `pi pi-server`. */
  icon: string
  children: ReactNode
  /** Optional control pinned to the right of the header (e.g. a status switch). */
  action?: ReactNode
  /**
   * Body padding. The default suits a field grid: the form controls carry
   * their own bottom margin, so the body trims its bottom padding to match.
   * Pass e.g. `divide-y divide-gray-100 px-5` for a list of toggle rows.
   */
  bodyClassName?: string
}

/**
 * A titled panel grouping related fields: icon chip, title and a one-line
 * description in the header, controls in the body.
 */
export function SectionCard({
  title,
  description,
  icon,
  children,
  action,
  bodyClassName = 'px-5 pt-5 pb-1',
}: SectionCardProps): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/70 px-5 py-3.5">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}
        >
          <i className={`${icon} text-xs`} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
        {action !== undefined && <div className="shrink-0">{action}</div>}
      </header>
      <div className={bodyClassName}>{children}</div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Tab strip
// ---------------------------------------------------------------------------

export interface DrawerTab {
  key: string
  label: string
  /** PrimeIcons class shown before the label. */
  icon: string
  /** Renders a red dot on the tab — use for "this tab holds a failing field". */
  error?: boolean
}

/**
 * A compact underlined tab strip for drawer forms.
 *
 * Nav only: the caller renders the panels and keeps them ALL MOUNTED (hiding
 * the inactive ones with the `hidden` attribute), so react-hook-form keeps
 * every field registered and an error on a background tab still exists in the
 * DOM. `error` on a tab is what tells the admin where to look.
 */
export function TabStrip({
  tabs,
  activeIndex,
  onChange,
  idPrefix,
  ariaLabel,
}: {
  tabs: DrawerTab[]
  activeIndex: number
  onChange: (index: number) => void
  /** Prefix for the tab/panel element ids; must match the caller's panels. */
  idPrefix: string
  ariaLabel: string
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="mb-6 flex items-center gap-1 border-b border-gray-200"
    >
      {tabs.map((tab, index) => {
        const isActive = index === activeIndex
        return (
          <button
            key={tab.key}
            id={`${idPrefix}-tab-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${tab.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(index)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault()
                onChange((index + 1) % tabs.length)
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault()
                onChange((index - 1 + tabs.length) % tabs.length)
              }
            }}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              isActive
                ? 'text-[#3454cf] border-[#3454cf]'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
            }`}
          >
            <i className={`${tab.icon} text-xs`} aria-hidden="true" />
            {tab.label}
            {tab.error === true && (
              <span
                className="h-1.5 w-1.5 rounded-full bg-red-500"
                aria-label="This tab has validation errors"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Convenience hook for a drawer's tab state plus per-tab error detection.
 * Pass the field names each tab owns; the returned `errorFor` reports whether
 * any of them is currently failing validation.
 */
export function useDrawerTabs(
  fieldsPerTab: readonly (readonly string[])[],
): {
  active: number
  setActive: (index: number) => void
  errorFor: (tabIndex: number) => boolean
} {
  const [active, setActive] = useState(0)
  const {
    formState: { errors },
  } = useFormContext()

  return {
    active,
    setActive,
    errorFor: (tabIndex: number) =>
      (fieldsPerTab[tabIndex] ?? []).some((f) => errors[f] !== undefined),
  }
}

// ---------------------------------------------------------------------------
// Boolean fields
// ---------------------------------------------------------------------------

/**
 * A boolean field as a horizontal row: label + description on the left, switch
 * on the right. Reads far better than a bare checkbox for prominent on/off
 * settings.
 *
 * The row owns its vertical padding and nothing else — no border, background
 * or shadow — so a stack of them inside a `SectionCard` reads as one divided
 * list rather than cards nested in a card. The description is capped at
 * `max-w-xl` so a two-line explanation can't stretch across a wide drawer and
 * crowd the switch.
 *
 * `onLabel`/`offLabel` add a state pill beside the switch, for toggles where
 * "on" deserves a word rather than just a slider position.
 */
export function SwitchRow({
  name,
  label,
  description,
  onLabel,
  offLabel,
  disabled = false,
}: {
  name: string
  label: string
  description: string
  onLabel?: string
  offLabel?: string
  /** Read-only rendering — used by view-mode drawers. */
  disabled?: boolean
}): JSX.Element {
  const { control } = useFormContext()
  const id = useId()
  const descId = `${id}-desc`

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const checked = Boolean(field.value)
        return (
          <div className="flex items-center justify-between gap-8 py-4">
            <div className="min-w-0">
              <label htmlFor={id} className="cursor-pointer text-sm font-semibold text-gray-900">
                {label}
              </label>
              <p id={descId} className="mt-1 max-w-xl text-xs leading-relaxed text-gray-500">
                {description}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {(onLabel ?? offLabel) !== undefined && (
                <StatePill checked={checked} onLabel={onLabel} offLabel={offLabel} />
              )}
              <InputSwitch
                inputId={id}
                checked={checked}
                onChange={(e) => field.onChange(e.value)}
                onBlur={field.onBlur}
                disabled={disabled}
                aria-describedby={descId}
              />
            </div>
          </div>
        )
      }}
    />
  )
}

/**
 * A boolean field rendered inline — label, state pill and switch side by side.
 * For toggles that belong in a `SectionCard` header rather than on a row of
 * their own; the explanatory text moves to `title` (tooltip).
 */
export function CompactSwitch({
  name,
  label,
  onLabel,
  offLabel,
  title,
  disabled = false,
}: {
  name: string
  label: string
  onLabel: string
  offLabel: string
  title: string
  /** Read-only rendering — used by view-mode drawers. */
  disabled?: boolean
}): JSX.Element {
  const { control } = useFormContext()
  const id = useId()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const checked = Boolean(field.value)
        return (
          <div className="flex items-center gap-2.5" title={title}>
            <label
              htmlFor={id}
              className={`text-xs font-semibold text-gray-600 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {label}
            </label>
            <StatePill checked={checked} onLabel={onLabel} offLabel={offLabel} />
            <InputSwitch
              inputId={id}
              checked={checked}
              onChange={(e) => field.onChange(e.value)}
              onBlur={field.onBlur}
              disabled={disabled}
            />
          </div>
        )
      }}
    />
  )
}

// ---------------------------------------------------------------------------
// Plain-state equivalents
//
// Some drawers can't use the react-hook-form pieces above: where a form embeds
// `DestinationSelector` (a self-contained controlled component with its own
// fetching, not an RHF field) the drawer keeps plain controlled state instead
// of a `ZodForm`. These render identically to their RHF counterparts but take
// value/onChange directly, so both kinds of drawer still look the same.
// ---------------------------------------------------------------------------

/** Spinner shown while an existing record is being fetched into a drawer. */
export function DrawerLoading({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
      <i className="pi pi-spin pi-spinner" aria-hidden="true" />
      {label}
    </div>
  )
}

/** `CompactSwitch` without form context — for a `SectionCard` header. */
export function PlainStatusSwitch({
  checked,
  disabled,
  onChange,
  label = 'Status',
  onLabel = 'Active',
  offLabel = 'Inactive',
  title = 'Inactive entries stay configured but are not used.',
}: {
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
  label?: string
  onLabel?: string
  offLabel?: string
  title?: string
}): JSX.Element {
  const id = useId()
  return (
    <div className="flex items-center gap-2.5" title={title}>
      <label htmlFor={id} className="cursor-pointer text-xs font-semibold text-gray-600">
        {label}
      </label>
      <StatePill checked={checked} onLabel={onLabel} offLabel={offLabel} />
      <InputSwitch
        inputId={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.value ?? false)}
      />
    </div>
  )
}

/** `SwitchRow` without form context. */
export function PlainSwitchRow({
  label,
  description,
  checked,
  disabled,
  onChange,
  onLabel,
  offLabel,
  icon,
  iconClassName,
}: {
  label: string
  description: string
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
  onLabel?: string
  offLabel?: string
  icon?: string
  iconClassName?: string
}): JSX.Element {
  const id = useId()
  const descId = `${id}-desc`
  return (
    <div className="flex items-center justify-between gap-8 py-4">
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              iconClassName ?? 'bg-gray-100 text-gray-500'
            }`}
          >
            <i className={icon} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <label htmlFor={id} className="cursor-pointer text-sm font-semibold text-gray-900">
            {label}
          </label>
          <p id={descId} className="mt-1 max-w-xl text-xs leading-relaxed text-gray-500">
            {description}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {(onLabel ?? offLabel) !== undefined && (
          <StatePill checked={checked} onLabel={onLabel} offLabel={offLabel} />
        )}
        <InputSwitch
          inputId={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.value ?? false)}
          aria-describedby={descId}
        />
      </div>
    </div>
  )
}

/**
 * Label + control + help/error, matching the spacing the RHF `Form*` wrappers
 * produce. Wrap a plain PrimeReact control in this inside a plain-state drawer.
 */
export function PlainField({
  label,
  htmlFor,
  required = false,
  error,
  help,
  children,
}: {
  label: string
  htmlFor: string
  required?: boolean
  error?: string | undefined
  help?: string | undefined
  children: ReactNode
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && (
          <span aria-hidden className="ml-1 text-red-500">
            *
          </span>
        )}
      </label>
      {children}
      {error !== undefined && error !== '' ? (
        <small role="alert" className="text-xs font-medium text-red-600">
          {error}
        </small>
      ) : (
        help !== undefined && <small className="text-xs text-gray-500">{help}</small>
      )}
    </div>
  )
}

function StatePill({
  checked,
  onLabel,
  offLabel,
}: {
  checked: boolean
  onLabel?: string | undefined
  offLabel?: string | undefined
}): JSX.Element {
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        checked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {checked ? (onLabel ?? 'On') : (offLabel ?? 'Off')}
    </span>
  )
}
